import type { CmsRegion, CmsRegionType } from '@/types/cms/region'

type StrapiRegion = {
    id: number
    attributes?: {
        name?: string
        order?: number | null
        type?: string
        icon?: {
            data?: {
                attributes?: {
                    url?: string
                }
            }
        }
    }
}

type StrapiResponse<T> = {
    data: T[]
}

const CMS_BASE_URL = import.meta.env.VITE_STRAPI_RS || ''
const REGION_ENDPOINT = '/api/regions'

const buildImageUrl = (path?: string | null) => {
    if (!path) return null
    if (path.startsWith('http')) return path
    if (!CMS_BASE_URL) return path
    return `${CMS_BASE_URL}${path}`
}

const buildRegionsUrl = (types?: CmsRegionType[]) => {
    const url = new URL(REGION_ENDPOINT, CMS_BASE_URL)
    const params = new URLSearchParams()

    params.append('populate', '*')
    if (types?.length) {
        types.forEach((type, index) => {
            params.append(`filters[type][$in][${index}]`, type)
        })
    }

    url.search = params.toString()
    return url.toString()
}

const mapRegion = (region: StrapiRegion): CmsRegion => {
    return {
        id: region.id,
        name: region.attributes?.name ?? '',
        order: region.attributes?.order ?? 0,
        type: (region.attributes?.type as CmsRegionType) ?? CmsRegionType.BOTH,
        iconUrl: buildImageUrl(region.attributes?.icon?.data?.attributes?.url)
    }
}

const getRegions = async (types?: CmsRegionType[]): Promise<CmsRegion[]> => {
    if (!CMS_BASE_URL) {
        throw new Error('CMS base URL is not configured')
    }
    const response = await fetch(buildRegionsUrl(types))
    if (!response.ok) {
        throw new Error('Failed to fetch regions')
    }
    const data = (await response.json()) as StrapiResponse<StrapiRegion>
    return data.data.map(mapRegion).sort((a, b) => a.order - b.order)
}

export const cmsRegionService = {
    getRegions
}
